import { useState } from 'react';
import { Modal, Avatar, useToast } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useUpdateMyProfile } from '@/hooks/queries';
import type { Profile, AvatarTone } from '@/types/domain';

const TONES: AvatarTone[] = ['sage', 'ochre', 'clay', 'slate'];

/**
 * Self-service profile edit. Only presentation fields (name + avatar colour) —
 * title / department / role / email are admin-managed (and the server RPC
 * whitelists exactly these fields, so the form can't escalate anything).
 */
export function EditProfileModal({ open, onClose, user }: { open: boolean; onClose: () => void; user: Profile }) {
  const { updateProfile } = useAuth();
  const { toast } = useToast();
  const save = useUpdateMyProfile();
  const [firstEn, setFirstEn] = useState(user.firstEn);
  const [lastEn, setLastEn] = useState(user.lastEn);
  const [firstTh, setFirstTh] = useState(user.firstTh);
  const [lastTh, setLastTh] = useState(user.lastTh);
  const [tone, setTone] = useState<AvatarTone>(user.tone);

  const valid = !!(firstEn.trim() && lastEn.trim() && firstTh.trim() && lastTh.trim());

  const submit = () => {
    const patch = { firstEn: firstEn.trim(), lastEn: lastEn.trim(), firstTh: firstTh.trim(), lastTh: lastTh.trim(), tone };
    save.mutate(patch, {
      onSuccess: () => {
        updateProfile(patch); // reflect immediately in the session
        toast('Profile updated', { icon: 'check' });
        onClose();
      },
      onError: () => toast('Could not save — please try again', { tone: 'clay' }),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit profile" width={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <Avatar initials={user.initials} size={64} tone={tone} photoUrl={user.photoUrl} />
          <div style={{ display: 'flex', gap: 10 }}>
            {TONES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTone(t)}
                aria-label={`Avatar colour ${t}`}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  padding: 0,
                  background: `var(--${t})`,
                  border: tone === t ? '2.5px solid var(--ink)' : '2px solid var(--line-2)',
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label className="field"><span className="label">First name (EN)</span><input className="input" value={firstEn} onChange={(e) => setFirstEn(e.target.value)} /></label>
          <label className="field"><span className="label">Last name (EN)</span><input className="input" value={lastEn} onChange={(e) => setLastEn(e.target.value)} /></label>
          <label className="field"><span className="label">ชื่อ (TH)</span><input className="input th" value={firstTh} onChange={(e) => setFirstTh(e.target.value)} /></label>
          <label className="field"><span className="label">นามสกุล (TH)</span><input className="input th" value={lastTh} onChange={(e) => setLastTh(e.target.value)} /></label>
        </div>

        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          Title, department, role and email are managed by an admin.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={!valid || save.isPending} onClick={submit} type="button">
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
